import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@shadcn/components/ui/form';
import type { SecretResponse } from '@shadcn/hooks/type';
import { useCreateSecrets, useDeleteSecrets, useGetSecrets } from '@shadcn/hooks/use-secrets';
import { secretsSchema } from '@shadcn/lib/schemas/secrets';
import { z } from 'zod';
import { ConfirmationDialog } from '~/components/dialogs/ConfirmationDialog/ConfirmationDialog.client';
import { Alert } from '~/components/ui/Alert';
import { Button } from '~/components/ui/Button';
import { Input } from '~/components/ui/Input';
import { Skeleton } from '~/components/ui/Skeleton';
import { formatDate } from 'date-fns';
import { menuStore } from '~/lib/stores/menu';

export function SecretsManager() {
  const { data: secrets, isLoading, error } = useGetSecrets();
  const { mutate: createSecrets, isPending: isCreating } = useCreateSecrets();
  const { mutate: deleteSecrets, isPending: isDeleting } = useDeleteSecrets();

  const [secretToDelete, setSecretToDelete] = useState<null | string>(null);

  const dialogContent = menuStore.dialogContent.get() as { type: 'settings'; prefilledSecrets?: string[] } | undefined;

  const form = useForm<z.infer<typeof secretsSchema>>({
    resolver: zodResolver(secretsSchema),
    defaultValues: {
      secrets: dialogContent?.prefilledSecrets?.map((secret) => ({ name: secret, value: '' })) ?? [{ name: '', value: '' }],
    },
  });

  const { fields } = useFieldArray({
    control: form.control,
    name: 'secrets',
  });

  const handleCreateSecrets = (formData: z.infer<typeof secretsSchema>) => {
    createSecrets(
      {
        secrets: formData.secrets,
      },
      {
        onSuccess: () => {
          form.reset({ secrets: [{ name: '', value: '' }] });
        },
      },
    );
  };

  const handleDelete = () => {
    if (secretToDelete) {
      deleteSecrets(
        {
          secretNames: [secretToDelete],
        },
        {
          onSuccess: () => {
            setSecretToDelete(null);
          },
        },
      );
    }
  };

  if (isLoading) {
    return (
      <div className="pt-12 space-y-4">
        <Skeleton className="h-12 w-full mb-8" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-12">
        <Alert type="danger" title="Error loading secrets">
          There was a problem loading your secrets configuration.
        </Alert>
      </div>
    );
  }

  return (
    <div className="pt-12">
      <div className="mb-6">
        <h2 className="text-base lg:text-xl font-semibold mb-2">Secrets</h2>
        <p className="text-bolt-ds-textSecondary mb-6 text-sm lg:text-base">View and manage your secrets.</p>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleCreateSecrets)} className="flex flex-col gap-4">
            {fields.map((field, index) => (
              <div key={field.id} className="flex items-start space-x-3">
                <FormField
                  control={form.control}
                  name={`secrets.${index}.name`}
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      {index === 0 && <FormLabel className="mb-4 text-bolt-ds-textSecondary">Name</FormLabel>}
                      <FormControl>
                        <Input
                          inputClassName="bg-bolt-ds-surfaceAltLight"
                          placeholder="Enter your secret name"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`secrets.${index}.value`}
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      {index === 0 && <FormLabel className="mb-4 text-bolt-ds-textSecondary">Value</FormLabel>}
                      <FormControl>
                        <Input
                          inputClassName="bg-bolt-ds-surfaceAltLight"
                          type="password"
                          placeholder="Enter your secret value"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            ))}

            <Button type="submit" disabled={isCreating} className="ml-auto" loading={isCreating}>
              Create secret
            </Button>
          </form>
        </Form>
      </div>

      <div>
        {secrets &&
          secrets.length > 0 &&
          secrets.map((secret: SecretResponse) => (
            <div
              key={secret.name}
              className="flex items-center justify-between py-4 border-b border-bolt-elements-borderColor last:border-b-0"
            >
              <div className="flex flex-col gap-1">
                <p className="text-bolt-ds-textPrimary text-sm font-medium">{secret.name}</p>
                {secret.updated_at && (
                  <p className="text-sm text-bolt-ds-textSecondary">
                    Last updated: {formatDate(new Date(secret.updated_at), 'MMMM dd, yyyy')} at{' '}
                    {formatDate(new Date(secret.updated_at), 'h:mm a')}
                  </p>
                )}
              </div>
              <Button
                color="danger"
                variant="link-color"
                onClick={() => {
                  setSecretToDelete(secret.name);
                }}
                disabled={isDeleting}
              >
                Delete
              </Button>
            </div>
          ))}
      </div>
      <ConfirmationDialog
        open={!!secretToDelete}
        titleText="Confirm deletion"
        handleConfirm={handleDelete}
        onClose={() => {
          setSecretToDelete(null);
        }}
        isConfirming={isDeleting}
        confirmButtonVariant="danger"
        confirmButtonNode="Delete"
      >
        Are you sure you want to delete the secret <b>{secretToDelete}</b>?
      </ConfirmationDialog>
    </div>
  );
}
